<template>
	<div class="container py-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/inventory/mixes"
				title="Back to custom mixes"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div class="flex-grow-1">
				<h1 class="mb-0">{{ mix?.name ?? "Custom Mix" }}</h1>
				<p v-if="mix" class="text-muted mb-0">
					Prepared batch:
					{{ formatAmount(mix.prepared_amount) }} {{ mix.prepared_unit }} ·
					resolution gives each component's share of the amount applied, with
					its registration numbers.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else-if="mix">
			<!-- The recipe: the whole mix, including the inert carriers the report
			     leaves out. Shares are computed over every component. -->
			<div class="card shadow-sm mb-4">
				<div class="card-body">
					<h2 class="h5 mb-3">Recipe</h2>
					<div class="table-responsive">
						<table class="table table-striped align-middle mb-0">
							<thead class="table-light">
								<tr>
									<th scope="col">Component</th>
									<th scope="col" class="text-end">Amount</th>
									<th scope="col">Unit</th>
									<th scope="col">Kind</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="component in mix.components" :key="component.id">
									<td>
										{{ component.name }}
										<span
											v-if="component.product_catalog_id"
											class="text-muted font-monospace small ms-1"
										>
											{{ component.product_catalog_id }}
										</span>
									</td>
									<td class="text-end">{{ formatAmount(component.amount) }}</td>
									<td>{{ component.unit }}</td>
									<td>
										<span
											class="badge rounded-pill"
											:class="
												component.is_inert ? 'bg-secondary' : 'bg-primary'
											"
										>
											{{ component.is_inert ? "Inert carrier" : "Product" }}
										</span>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- Resolution: the amount applied is decomposed across the product
			     components. Inert carriers carry no registration and are excluded. -->
			<div class="card shadow-sm">
				<div class="card-body">
					<h2 class="h5 mb-3">Resolve for an Amount</h2>

					<form
						class="row g-2 align-items-end mb-3"
						@submit.prevent="resolveReport"
					>
						<div class="col-12 col-md-3">
							<label class="form-label" for="reportAmount"
								>Amount applied</label
							>
							<input
								id="reportAmount"
								v-model.number="amount"
								type="number"
								min="0"
								step="any"
								class="form-control"
								required
							/>
						</div>
						<div class="col-12 col-md-3">
							<label class="form-label" for="reportUnit">Unit</label>
							<input
								id="reportUnit"
								v-model="unit"
								type="text"
								class="form-control"
								:placeholder="mix.prepared_unit"
							/>
						</div>
						<div class="col-12 col-md-auto">
							<ButtonLoading
								type="submit"
								text="Resolve"
								icon="bi bi-calculator"
								variant="primary"
								:loading="resolving"
								:disabled="!(amount > 0)"
							/>
						</div>
					</form>

					<div v-if="report" class="table-responsive">
						<div class="text-muted small mb-2">
							Resolved {{ formatAmount(report.applied_amount) }}
							{{ report.applied_unit }} of the
							{{ formatAmount(report.prepared_amount) }}
							{{ report.prepared_unit }} prepared — fraction
							{{ formatAmount(report.fraction) }} — across
							{{ report.components.length }} product component{{
								report.components.length === 1 ? "" : "s"
							}}. Inert carriers are excluded — they are not product.
						</div>
						<table class="table table-striped align-middle">
							<thead class="table-light">
								<tr>
									<th scope="col">Product</th>
									<th scope="col" class="text-end">Share</th>
									<th scope="col" class="text-end">Quantity</th>
									<th scope="col">EPA registration</th>
									<th scope="col">DPR registration</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="component in report.components"
									:key="component.product_catalog_id"
								>
									<td>{{ component.product_name }}</td>
									<td class="text-end">{{ formatPercent(component.share) }}</td>
									<td class="text-end">
										{{ formatAmount(component.quantity) }} {{ component.unit }}
									</td>
									<td class="font-monospace">
										{{ component.epa_registration || "—" }}
									</td>
									<td class="font-monospace">
										{{ component.dpr_registration_number || "—" }}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</template>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type { InventoryMix, InventoryMixReport } from "@/type/api";

const route = useRoute();
// The route passes no props; the mix id is the path segment (contract §8).
const mixId = parseInt(route.params.id as string, 10);

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const mix = ref<InventoryMix | null>(null);
const loading = ref(false);
const error = ref("");

const amount = ref(1);
// The report's unit defaults to the recipe's prepared unit; an explicit unit
// must share that dimension or the server rejects the request.
const unit = ref("");
const report = ref<InventoryMixReport | null>(null);
const resolving = ref(false);

function formatAmount(value: number): string {
	if (Number.isInteger(value)) return String(value);
	return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

function formatPercent(share: number): string {
	return `${(share * 100).toFixed(2).replace(/\.00$/, "")}%`;
}

// There is no get-by-id on the mix list, so the mix is found in the list the
// server returns through the generic QUERY endpoint (the legacy GET list's
// replacement, doc/in-progress/query.md §11 Phase 4); a missing id is reported
// rather than shown as an empty mix.
async function load() {
	loading.value = true;
	error.value = "";
	try {
		const page =
			await apiClient.JSONQueryAll<InventoryMix>("/api/inventory/mix");
		mix.value = page.items.find((row) => row.id === mixId) ?? null;
		if (!mix.value) {
			error.value = "This mix was not found.";
			showNotification("Mix not found", error.value);
		} else {
			unit.value = mix.value.prepared_unit;
		}
	} catch (e: any) {
		error.value = e?.message || "Failed to load this mix.";
		showNotification("Failed to load mix", error.value);
	} finally {
		loading.value = false;
	}
}

async function resolveReport() {
	if (!mix.value || !(amount.value > 0)) return;
	resolving.value = true;
	const params: Record<string, number | string> = { amount: amount.value };
	const requestedUnit = unit.value.trim();
	if (requestedUnit !== "") params.unit = requestedUnit;
	try {
		report.value = await apiClient.JSONGet<InventoryMixReport>(
			`/api/inventory/mix/${mix.value.id}/report`,
			{ params },
		);
	} catch (e: any) {
		showNotification(
			"Failed to resolve the report",
			e?.message || "Please try again.",
		);
	} finally {
		resolving.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
